import {
  type AITrace,
  hasAITrace,
  type MultiCurveCandidate,
  useAsyncOperation,
  useDemandData,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import Markdown from "markdown-to-jsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import Loading from "../Loading";
import CollapsibleCard from "../site/CollapsibleCard";
import DemandFileConfig, {
  FileMapping,
  Filter,
  SheetConfig,
} from "./DemandFileConfig";
import { HhDataTempFile, OutputMetaData } from "./DemandFileSummaryRow";
import DemandFileSummaryTable from "./DemandFileSummaryTable";
import GraphDemandData from "./GraphDemandData";
import MultiCurveSplitModal from "./MultiCurveSplitModal";

interface DemandFileProcessModalProps {
  file: HhDataTempFile;
  config: FileMapping;
  loadingConfig: boolean;
  onClose: () => void;
  onProcessComplete: () => void;
  // When opening Process on a row whose Auto-process LRO failed during a bulk run,
  // seed the modal with that LRO's operationKey so useAsyncOperation immediately picks
  // up the persisted error + logPayload and surfaces the "Auto-fix with AI" alert.
  initialOperationKey?: string | null;
  // When opening via [Review file analysis], the already-computed analysis (incl.
  // trace). Renders instantly and suppresses the auto-trigger so we don't re-run
  // the AI for an analysis the operator has already seen.
  initialErrorAnalysis?: {
    summary: string;
    details: string;
    proposed_config?: Record<string, unknown> | null;
    proposed_config_rationale?: string | null;
    trace?: AITrace | null;
  } | null;
  // Called when a process / auto-process is kicked off from inside the modal, so the
  // parent can reflect the in-flight LRO in the main table (spinner + polling +
  // coach-on-failure) - the operator can close the modal and still see progress.
  onProcessingStarted?: (fileGuid: string, operationKey: string) => void;
}

interface ProcessHhDataResult {
  outputMetaData?: OutputMetaData;
  logPayload?: Record<string, unknown>;
}

const DemandFileProcessModal = ({
  file,
  config,
  loadingConfig,
  onClose,
  onProcessComplete,
  initialOperationKey = null,
  initialErrorAnalysis = null,
  onProcessingStarted,
}: DemandFileProcessModalProps) => {
  const {
    processHhDataTemp,
    processHhDataTempAuto,
    fetchHhDataTempLogs,
    fetchHhDataTempErrorAnalysis,
    detectMultiCurve,
  } = useDemandData();
  const [fileState, setFileState] = useState<HhDataTempFile>(file);
  const [editMode, setEditMode] = useState<"ui" | "json">("ui");
  const [mapping, setMapping] = useState<FileMapping>(config);
  const [uiSheets, setUiSheets] = useState<SheetConfig[]>(
    config.sheets && config.sheets.length > 0 ? config.sheets : [{}]
  );
  const [jsonValue, setJsonValue] = useState<string>(
    JSON.stringify(config, null, 2)
  );
  const [jsonError, setJsonError] = useState<string>("");
  const [discardZeroValuesToggled, setDiscardZeroValuesToggled] =
    useState<boolean>(config.overrides?.discard_zero_values !== undefined);
  const [hasProcessed, setHasProcessed] = useState<boolean>(
    !!file.outputMetaData
  );
  const [logPayload, setLogPayload] = useState<any>(null);
  const [logsContent, setLogsContent] = useState<string>("");
  const [hasFetchedLogs, setHasFetchedLogs] = useState<boolean>(false);
  const [selectedTempFiles, setSelectedTempFiles] = useState<HhDataTempFile[]>(
    []
  );
  const [operationKey, setOperationKey] = useState<string | null>(
    initialOperationKey
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorAnalysis, setErrorAnalysis] = useState<{
    summary: string;
    details: string;
    proposed_config?: Record<string, unknown> | null;
    proposed_config_rationale?: string | null;
    trace?: AITrace | null;
    cached?: boolean;
  } | null>(initialErrorAnalysis);
  // True once an analysis result is in hand (seeded via Review, or fetched). Used to
  // disable + relabel the "Auto-fix with AI" button to "AI analysis complete".
  const [analysisComplete, setAnalysisComplete] = useState<boolean>(
    !!initialErrorAnalysis
  );
  const [multiCurveDetection, setMultiCurveDetection] = useState<{
    candidates: MultiCurveCandidate[];
    reason: string;
  } | null>(null);

  // fileGuid is used as the DynamoDB partition key since there's no site context
  const asyncOperation = useAsyncOperation({
    siteId: fileState.fileGuid,
    operationKey,
  });

  // Auto-trigger AI analysis once per failed operationKey. The cached BE result means
  // re-opening a modal for a previously-analysed failure returns the prior summary
  // instantly (the cache key is (content_hash, config_hash, model_id) in
  // error_analysis.py - identical inputs hit the same S3 object).
  const autoAnalysedOperationKey = useRef<string | null>(null);
  const handleAutoFixWithAIRef = useRef<((force: boolean) => void) | null>(
    null
  );

  // Handle async operation completion
  useEffect(() => {
    if (!asyncOperation.data) return;

    if (asyncOperation.data.status === "completed") {
      const result = asyncOperation.data.result as
        | ProcessHhDataResult
        | undefined;
      setOperationKey(null);

      if (result?.outputMetaData) {
        const updatedFile = {
          ...fileState,
          outputMetaData: result.outputMetaData,
        };
        setFileState(updatedFile);
        setSelectedTempFiles([updatedFile]);
      }

      if (result?.logPayload) {
        setLogPayload(result.logPayload);
      }

      setHasProcessed(true);
      onProcessComplete();
    } else if (asyncOperation.data.status === "failed") {
      // Capture the operationKey BEFORE clearing it, so the auto-trigger effect
      // below can deduplicate against re-renders of the same failure.
      const failedOperationKey = operationKey;
      setOperationKey(null);

      const error = asyncOperation.data.error || "Processing failed.";
      setErrorMessage(error);

      const failedResult = asyncOperation.data.result as
        | ProcessHhDataResult
        | undefined;
      if (failedResult?.logPayload) {
        setLogPayload(failedResult.logPayload);
      }

      // Auto-fire the AI analysis exactly once per failed LRO. Guarded by ref so
      // a re-mount with the same initialOperationKey doesn't double-fire. The BE
      // serves cached results when input/config hashes match, so this is cheap on
      // repeat opens.
      //
      // The instant-review path ([Review file analysis]) seeds initialErrorAnalysis
      // and we must NOT re-run the AI for that already-seen failure. But that
      // suppression applies only to the *initial* failure: once the operator
      // reprocesses from inside the modal, the new LRO has a different operationKey
      // and its failure is a fresh analysis target, so we let it fire. Without this,
      // a reprocess-after-review that fails left no path to a new analysis at all.
      const isAlreadyReviewedInitialFailure =
        !!initialErrorAnalysis && failedOperationKey === initialOperationKey;
      if (
        !isAlreadyReviewedInitialFailure &&
        failedOperationKey &&
        autoAnalysedOperationKey.current !== failedOperationKey
      ) {
        autoAnalysedOperationKey.current = failedOperationKey;
        // Defer to allow setErrorMessage/setLogPayload to settle first; the
        // handler is defined further down so we use a microtask deferral rather
        // than calling synchronously.
        queueMicrotask(() => handleAutoFixWithAIRef.current?.(false));
      }
    }
  }, [
    asyncOperation.data,
    fileState,
    onProcessComplete,
    operationKey,
    initialErrorAnalysis,
    initialOperationKey,
  ]);

  // Handle timeout
  useEffect(() => {
    if (asyncOperation.timedOut) {
      setOperationKey(null);
      setErrorMessage("Processing timed out after 15 minutes.");
    }
  }, [asyncOperation.timedOut]);

  const isProcessing =
    !!operationKey ||
    processHhDataTemp.isLoading ||
    processHhDataTempAuto.isLoading ||
    detectMultiCurve.isLoading;

  // Update fileState when file prop changes
  useEffect(() => {
    setFileState(file);
  }, [file]);

  // Update config state when config prop changes
  useEffect(() => {
    setMapping(config);
    setUiSheets(
      config.sheets && config.sheets.length > 0 ? config.sheets : [{}]
    );
    setJsonValue(JSON.stringify(config, null, 2));
    setDiscardZeroValuesToggled(
      config.overrides?.discard_zero_values !== undefined
    );
  }, [config]);

  // Auto-select file for graphing when it has been processed
  useEffect(() => {
    if (
      hasProcessed &&
      fileState.outputMetaData &&
      selectedTempFiles.length === 0
    ) {
      setSelectedTempFiles([fileState]);
    }
  }, [hasProcessed, fileState, selectedTempFiles.length]);

  // Sync mapping to jsonValue when mapping changes in UI mode
  const updateMapping = (updates: Partial<FileMapping>) => {
    const newMapping = { ...mapping, ...updates };

    // Update uiSheets if sheets are being updated
    if ("sheets" in updates) {
      setUiSheets(updates.sheets || []);
    }

    // Clean up filters: only include filters with both column and value/not_value
    if (newMapping.sheets) {
      newMapping.sheets = newMapping.sheets.map((sheet) => {
        if (sheet.filters) {
          const validFilters = sheet.filters.filter(
            (f) => f.column && (f.value || f.not_value)
          );
          return {
            ...sheet,
            filters: validFilters.length > 0 ? validFilters : undefined,
          };
        }
        return sheet;
      });
    }

    setMapping(newMapping);
    setJsonValue(JSON.stringify(newMapping, null, 2));
  };

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    try {
      const parsed = JSON.parse(value);
      setMapping(parsed);
      setUiSheets(parsed.sheets || []);
      setJsonError("");
    } catch (error) {
      setJsonError("Invalid JSON");
    }
  };

  const handleSubmit = async (configOverride?: FileMapping) => {
    if (!configOverride && editMode === "json" && jsonError) {
      alert("Please fix JSON errors before submitting");
      return;
    }

    // Reset state
    setLogsContent("");
    setHasFetchedLogs(false);
    setErrorMessage(null);
    setLogPayload(null);
    setErrorAnalysis(null);
    // analysisComplete tracks the CURRENT attempt's analysis. Clearing it alongside
    // errorAnalysis keeps the "Auto-fix with AI" button in step: otherwise a reprocess
    // (e.g. "Apply proposed config and re-process") leaves the button stuck reading
    // "AI analysis complete" with no analysis card, and disabled so it can't be retried.
    setAnalysisComplete(false);

    // configOverride is used when the operator clicks "Apply proposed config and
    // re-process" - we don't want to wait for setState(mapping) to settle before
    // firing the mutation.
    const configToSend = configOverride ?? mapping;

    try {
      const response = await processHhDataTemp.mutateAsync({
        fileGuid: fileState.fileGuid,
        config: configToSend,
        // configOverride is only passed by "Apply proposed config and re-process"
        // (the AI fix); a plain manual Process passes no override.
        source: configOverride ? "ai_fix" : "manual",
      });

      const responseData = response as any;
      setOperationKey(responseData.operationKey);
      if (responseData.operationKey) {
        onProcessingStarted?.(fileState.fileGuid, responseData.operationKey);
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      setErrorMessage(
        errorData?.message || "Failed to start processing."
      );
    }
  };

  const handleAutoSubmit = async () => {
    // Reset all error/log/analysis state from any prior attempt on this modal
    setLogsContent("");
    setHasFetchedLogs(false);
    setErrorMessage(null);
    setLogPayload(null);
    setErrorAnalysis(null);
    setAnalysisComplete(false);

    // First check whether the file is multi-curve (sheet-per-site or column-per-site).
    // If so, show the split modal and let the operator pick which curves to materialise.
    try {
      const detection = await detectMultiCurve.mutateAsync(fileState.fileGuid);
      if (detection.is_multi_curve && detection.candidates.length > 0) {
        setMultiCurveDetection({
          candidates: detection.candidates,
          reason: detection.reason,
        });
        return;
      }
    } catch (error) {
      // Detection is best-effort - log a warning and proceed with the single-curve Auto path
      console.warn("multi-curve detection failed:", error);
    }

    try {
      const response = await processHhDataTempAuto.mutateAsync(
        fileState.fileGuid
      );
      // process_auto returns no operationKey for a multi-curve file, but this path
      // is only reached after the modal's own detectMultiCurve pre-check passed, so
      // a single-curve LRO key is expected here. Guard for the type regardless.
      if (response.operationKey) {
        setOperationKey(response.operationKey);
        onProcessingStarted?.(fileState.fileGuid, response.operationKey);
      }
    } catch (error: any) {
      const errorData = error?.response?.data;
      setErrorMessage(
        errorData?.message || "Failed to start auto-processing."
      );
    }
  };

  const handleSplitComplete = () => {
    setMultiCurveDetection(null);
    onClose();
  };

  const handleFetchLogs = async () => {
    if (!logPayload) {
      alert("No log payload available. Please process the file first.");
      return;
    }

    try {
      const result = await fetchHhDataTempLogs.mutateAsync({
        fileGuid: fileState.fileGuid,
        logPayload,
      });
      setLogsContent(result.logsContent);
      setHasFetchedLogs(true);
    } catch (error) {
      alert(`Error fetching logs: ${error}`);
    }
  };

  const handleAutoFixWithAI = async (forceRefresh = false) => {
    setErrorAnalysis(null);
    // Analysis is now in flight, so it is no longer "complete". Only the success path
    // below flips this back to true. This keeps the failure/error paths (coach returns
    // {error}, or the request throws) from leaving the button stuck on a disabled
    // "AI analysis complete" with no card - it falls back to a retriable "Auto-fix with AI".
    setAnalysisComplete(false);
    try {
      const result = await fetchHhDataTempErrorAnalysis.mutateAsync({
        fileGuid: fileState.fileGuid,
        request: {
          logPayload: logPayload || undefined,
          config: mapping as Record<string, unknown>,
          forceRefresh,
        },
      });
      if (result.error) {
        alert(`Error coach unavailable: ${result.error}`);
        return;
      }
      setErrorAnalysis({
        summary: result.summary ?? "",
        details: result.details ?? "",
        proposed_config: result.proposed_config,
        proposed_config_rationale: result.proposed_config_rationale,
        trace: result.trace,
        cached: result.cached,
      });
      setAnalysisComplete(true);
    } catch (error: any) {
      alert(`Error coach failed: ${error?.response?.data?.error || error}`);
    }
  };

  // Keep the ref pointing at the latest closure so the auto-trigger effect in the
  // failed-LRO branch (defined above) sees the current mapping / logPayload state.
  handleAutoFixWithAIRef.current = handleAutoFixWithAI;

  const handleApplyProposedConfigAndReprocess = async () => {
    if (!errorAnalysis?.proposed_config) return;
    const proposed = errorAnalysis.proposed_config as Partial<FileMapping>;
    // Validate the shape - Bedrock could return something malformed. Fall back to
    // showing the JSON in the editor so the operator can inspect rather than crashing.
    if (
      typeof proposed !== "object" ||
      proposed === null ||
      (proposed.sheets && !Array.isArray(proposed.sheets))
    ) {
      alert(
        "Proposed config doesn't match the expected FileMapping shape - " +
          "opening it in the JSON editor for you to review."
      );
    }
    const safeMapping: FileMapping = {
      sheets: Array.isArray(proposed.sheets) ? proposed.sheets : [],
      overrides:
        typeof proposed.overrides === "object" && proposed.overrides !== null
          ? proposed.overrides
          : undefined,
      time_zone: proposed.time_zone,
    };
    // Update the visible mapping/editor (so the operator can see what's running)
    // AND fire the manual /process with the proposed config in the same click.
    // Passing safeMapping explicitly avoids a stale-mapping race with setState.
    setMapping(safeMapping);
    setUiSheets(safeMapping.sheets || []);
    setJsonValue(JSON.stringify(safeMapping, null, 2));
    await handleSubmit(safeMapping);
  };

  const addSheet = () => {
    const sheets = [...uiSheets, {}];
    updateMapping({ sheets });
  };

  const updateSheet = (sheetIndex: number, updates: Partial<SheetConfig>) => {
    const sheets = [...uiSheets];
    sheets[sheetIndex] = { ...sheets[sheetIndex], ...updates };
    updateMapping({ sheets });
  };

  const removeSheet = (sheetIndex: number) => {
    const sheets = uiSheets.filter((_, i) => i !== sheetIndex);
    updateMapping({
      sheets: sheets.length > 0 ? sheets : undefined,
    });
  };

  const addFilter = (sheetIndex: number) => {
    const sheets = [...uiSheets];
    const filters = sheets[sheetIndex]?.filters || [];
    sheets[sheetIndex] = {
      ...sheets[sheetIndex],
      filters: [...filters, {}],
    };
    updateMapping({ sheets });
  };

  const updateFilter = (
    sheetIndex: number,
    filterIndex: number,
    updates: Partial<Filter>
  ) => {
    const sheets = [...uiSheets];
    const filters = [...(sheets[sheetIndex]?.filters || [])];
    filters[filterIndex] = { ...filters[filterIndex], ...updates };
    sheets[sheetIndex] = { ...sheets[sheetIndex], filters };
    updateMapping({ sheets });
  };

  const removeFilter = (sheetIndex: number, filterIndex: number) => {
    const sheets = [...uiSheets];
    const filters = (sheets[sheetIndex]?.filters || []).filter(
      (_, i) => i !== filterIndex
    );
    sheets[sheetIndex] = {
      ...sheets[sheetIndex],
      filters: filters.length > 0 ? filters : undefined,
    };
    updateMapping({ sheets });
  };

  // Memoize empty array and map to prevent unnecessary re-renders
  const emptyHhDataItems = useMemo(() => [], []);
  const emptySiteMap = useMemo(() => new Map(), []);
  const handleClearSelection = useCallback(() => setSelectedTempFiles([]), []);

  if (loadingConfig) {
    return (
      <Modal show onHide={onClose} size="xl" style={{ marginTop: "5vh" }}>
        <Modal.Body>
          <Loading label="Loading configuration..." height="20vh" />
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      dialogClassName="modal-90w"
      style={{ marginTop: "5vh" }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Process File: {fileState.fileName}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {/* Processing status */}
        {isProcessing && (
          <Alert variant="info" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>
              Processing...{" "}
              {asyncOperation.data?.status === "running"
                ? "(running)"
                : "(pending)"}
            </span>
          </Alert>
        )}

        {/* Error message */}
        {errorMessage && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setErrorMessage(null)}
          >
            <div className="d-flex justify-content-between align-items-start gap-2">
              <span>{errorMessage}</span>
              <Button
                size="sm"
                variant="warning"
                onClick={() => handleAutoFixWithAI(false)}
                disabled={
                  fetchHhDataTempErrorAnalysis.isLoading || analysisComplete
                }
              >
                {fetchHhDataTempErrorAnalysis.isLoading ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                    Auto-fixing...
                  </>
                ) : analysisComplete ? (
                  "AI analysis complete"
                ) : (
                  "Auto-fix with AI"
                )}
              </Button>
            </div>
          </Alert>
        )}

        {/* AI analysis in flight - Bedrock multi-tool loops can take 30-60s, so make
            the wait visible rather than leaving the operator wondering. */}
        {fetchHhDataTempErrorAnalysis.isLoading && (
          <Alert variant="info" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>
              AI is analysing the failure - this can take up to a minute...
            </span>
          </Alert>
        )}

        {/* Error coach analysis */}
        {errorAnalysis && (
          <Alert variant="info" className="mt-2">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div style={{ flex: 1 }}>
                <strong>AI analysis</strong>
                {errorAnalysis.cached && (
                  <span className="badge bg-secondary ms-2">cached</span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => handleAutoFixWithAI(true)}
                disabled={fetchHhDataTempErrorAnalysis.isLoading}
              >
                {fetchHhDataTempErrorAnalysis.isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Re-running...
                  </>
                ) : (
                  "Re-run auto-fix"
                )}
              </Button>
            </div>
            <div
              className="mt-2 ai-analysis-markdown"
              style={{ fontSize: "0.95em" }}
            >
              {/* Inline style block keeps the markdown rendering legible inside
                  the Bootstrap Alert without blowing up heading sizes or list
                  indents. Scoped via .ai-analysis-markdown so it doesn't bleed. */}
              <style>{`
                .ai-analysis-markdown h1,
                .ai-analysis-markdown h2,
                .ai-analysis-markdown h3 { font-size: 1.05em; font-weight: 600; margin: 0.6em 0 0.2em; }
                .ai-analysis-markdown p { margin: 0 0 0.6em; }
                .ai-analysis-markdown ul, .ai-analysis-markdown ol { margin: 0 0 0.6em; padding-left: 1.5em; }
                .ai-analysis-markdown code { background: rgba(0,0,0,0.06); padding: 0 0.25em; border-radius: 3px; }
                .ai-analysis-markdown pre { background: rgba(0,0,0,0.06); padding: 0.5em; border-radius: 4px; overflow-x: auto; }
                .ai-analysis-markdown pre code { background: transparent; padding: 0; }
              `}</style>
              {/* Prefer the model's terminal explanation (`details`). When the model
                  ended its turn on the propose_config call with no trailing text,
                  `details` is empty but the explanation lives in the proposed-config
                  rationale - fall back to it so the card is never blank. */}
              <Markdown>
                {errorAnalysis.details ||
                  errorAnalysis.proposed_config_rationale ||
                  ""}
              </Markdown>
            </div>
            {errorAnalysis.proposed_config && (
              <div className="mt-3">
                {/* The proposed_config_rationale (the model's terminal tool-call
                    justification) duplicates the plain-English explanation in
                    `details`, so we don't render it again - one explanation only. */}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleApplyProposedConfigAndReprocess}
                  disabled={isProcessing}
                >
                  {isProcessing
                    ? "Re-processing..."
                    : "Apply proposed config and re-process"}
                </Button>
              </div>
            )}
            {hasAITrace(errorAnalysis.trace) && (
              <details className="mt-3">
                <summary
                  style={{ cursor: "pointer", fontSize: "0.85em" }}
                  className="text-muted"
                >
                  Show full AI analysis trace (
                  {errorAnalysis.trace.entries.length} steps)
                </summary>
                <div className="mt-2">
                  <AITraceViewer trace={errorAnalysis.trace} />
                </div>
              </details>
            )}
          </Alert>
        )}

        {/* Graph - only shown after processing succeeds */}
        {hasProcessed && fileState.outputMetaData && (
          <GraphDemandData
            selectedTempFiles={selectedTempFiles}
            selectedHhDataItems={emptyHhDataItems}
            siteMap={emptySiteMap}
            onClearSelection={handleClearSelection}
            autoEnable={true}
            includeFullRange={true}
          />
        )}

        {/* Results Card - only shown after processing succeeds */}
        {hasProcessed && fileState.outputMetaData && (
          <Card className="mt-3 mb-3">
            <Card.Header>
              <strong>Results</strong>
            </Card.Header>
            <Card.Body>
              <DemandFileSummaryTable files={[fileState]} showActions={false} />
              {(() => {
                const md = fileState.outputMetaData;
                if (!md) return null;
                const isUtc = !!md.timestamps_are_utc;
                const det = md.timestamp_detection;
                let detail: string;
                if (det) {
                  detail =
                    det.verdict === "inconclusive"
                      ? `auto-detection inconclusive${det.reason ? `: ${det.reason}` : ""}`
                      : `auto-detected${
                          det.confidence_ratio != null
                            ? `, confidence ${det.confidence_ratio}`
                            : ""
                        }`;
                } else {
                  detail = isUtc ? "manual override" : "default";
                }
                return (
                  <div
                    className="mt-2 text-muted"
                    style={{ fontSize: "0.9em" }}
                  >
                    Timestamp interpretation:{" "}
                    <strong>{isUtc ? "UTC" : "Local time"}</strong> ({detail})
                  </div>
                );
              })()}
            </Card.Body>
          </Card>
        )}

        {/* Logs Card - shown when logPayload is available (even on failure) */}
        {logPayload && (
          <CollapsibleCard title="Logs" className="mb-3" initialOpen={true}>
            <div className="d-flex flex-column gap-3">
              <div>
                <Button
                  variant="secondary"
                  onClick={handleFetchLogs}
                  disabled={fetchHhDataTempLogs.isLoading || isProcessing}
                >
                  {fetchHhDataTempLogs.isLoading
                    ? "Fetching..."
                    : hasFetchedLogs
                      ? "Refresh logs"
                      : "Fetch logs"}
                </Button>
              </div>
              {logsContent && (
                <Form.Control
                  as="textarea"
                  rows={15}
                  value={logsContent}
                  readOnly
                  style={{ fontFamily: "monospace", fontSize: "12px" }}
                />
              )}
            </div>
          </CollapsibleCard>
        )}

        {/* Config Card - collapsed by default now that Auto-process is the primary path. */}
        <CollapsibleCard
          title="Config (manual)"
          className="mb-3"
          initialOpen={false}
        >
          <DemandFileConfig
            mapping={mapping}
            uiSheets={uiSheets}
            jsonValue={jsonValue}
            jsonError={jsonError}
            editMode={editMode}
            discardZeroValuesToggled={discardZeroValuesToggled}
            readOnly={false}
            onEditModeChange={setEditMode}
            onMappingChange={updateMapping}
            onJsonChange={handleJsonChange}
            onSheetAdd={addSheet}
            onSheetUpdate={updateSheet}
            onSheetRemove={removeSheet}
            onFilterAdd={addFilter}
            onFilterUpdate={updateFilter}
            onFilterRemove={removeFilter}
            onDiscardZeroValuesToggle={setDiscardZeroValuesToggled}
          />
        </CollapsibleCard>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleAutoSubmit}
          disabled={isProcessing}
          title="Auto-detect period + unit and process with unlimited gap fill"
        >
          {isProcessing ? "Processing..." : "Auto-process"}
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => handleSubmit()}
          disabled={isProcessing || (editMode === "json" && !!jsonError)}
        >
          {isProcessing ? "Processing..." : "Process (manual config)"}
        </Button>
      </Modal.Footer>

      {multiCurveDetection && (
        <MultiCurveSplitModal
          file={fileState}
          candidates={multiCurveDetection.candidates}
          reason={multiCurveDetection.reason}
          onClose={() => setMultiCurveDetection(null)}
          onSplitComplete={handleSplitComplete}
        />
      )}
    </Modal>
  );
};


export default DemandFileProcessModal;
